import { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import useStores from 'shared/hooks/use-stores';
import { getProductWeight } from 'shared/helpers/products';
import { ApolloError } from '@apollo/client';
import { useCheckAllotmentQuery, GqlAllotmentInfo, GqlCartItemInput, AllotmentStatus } from 'types/graphql';

// Mirrors the MobX CartItem class shape (state/cart-item.js) used by mapInputsForPriceCartAPI.
// TODO: Remove once CartItem and CartStore are typed.
type CartItem = {
  product: {
    POSMetaData: {
      canonicalID?: string;
      children?: Array<{ option: string; canonicalID?: string; packageQuantity?: number }>;
    };
  };
  option: string;
  key: string;
  quantity: number;
  basePrice: number;
};

type AllotmentCheckResult = {
  allotmentInfo: GqlAllotmentInfo | null;
  loading: boolean;
  error: ApolloError | undefined;
  isAllotmentExceeded: boolean;
  hasAllotmentError: boolean;
  shouldShowAllotment: boolean;
  shouldBlockOrder: boolean;
};

function mapCartItemsForAllotment(cart: Record<string, CartItem>): GqlCartItemInput[] {
  return Object.values(cart).reduce<GqlCartItemInput[]>((cartItems, item) => {
    const children = item.product.POSMetaData.children ?? [];
    const childProduct = children.find((child) => child.option === item.option);
    const productId = Number(childProduct?.canonicalID ?? item.product.POSMetaData.canonicalID);

    if (!productId) {
      return cartItems;
    }

    cartItems.push({
      productId,
      ecommKey: item.key,
      quantity: childProduct?.packageQuantity ? childProduct.packageQuantity * item.quantity : item.quantity,
      unitPrice: item.basePrice,
      unitWeightGrams: getProductWeight(item.option, 0, 1) / (childProduct?.packageQuantity ?? 1),
    });

    return cartItems;
  }, []);
}

export function useAllotmentCheck(): AllotmentCheckResult {
  const { Cart, User } = useStores();
  const flags = useFlags();

  const { order, dispensary } = Cart;
  const isFloridaMedical = dispensary?.location?.state === 'FL' && order.medicalOrder;
  const floridaAllotmentCheckEnabled = flags['ecomm.fl-mmur-allotment-check.rollout'] ?? false;
  const shouldCheck = isFloridaMedical && floridaAllotmentCheckEnabled;
  const medicalCardNumber = Cart.order.medicalCard?.number;
  const dateOfBirth = User.birthday;

  const cartItems = useMemo(() => {
    if (!shouldCheck || !medicalCardNumber || !dateOfBirth) {
      return [];
    }
    return mapCartItemsForAllotment(order.cart);
  }, [shouldCheck, medicalCardNumber, dateOfBirth, order.cart]);

  const { data, loading, error } = useCheckAllotmentQuery({
    variables: {
      input: {
        dispensaryId: dispensary?.id,
        medicalCardNumber,
        dateOfBirth,
        cart: cartItems,
      },
    },
    skip: !shouldCheck || !medicalCardNumber || !dateOfBirth || cartItems.length === 0,
    fetchPolicy: 'cache-and-network',
  });

  const allotmentInfo = data?.checkAllotment ?? null;
  const isAllotmentExceeded = allotmentInfo?.status === AllotmentStatus.exceeded;
  const hasUserFacingError = Boolean(allotmentInfo?.error);
  const hasAllotmentError = Boolean(error) || hasUserFacingError;
  const blockOrderOnAllotmentFailure = flags['ecomm.fl-mmur-block-order-on-allotment-failure.rollout'] ?? false;
  // Block on exceeded or user-facing errors (e.g. expired card), but not on system errors
  // (e.g. BioTrack down) — let the customer proceed and verify at the store.
  const shouldBlockOrder = blockOrderOnAllotmentFailure && (isAllotmentExceeded || hasUserFacingError);

  return {
    allotmentInfo,
    loading: shouldCheck && loading,
    error,
    isAllotmentExceeded,
    hasAllotmentError,
    shouldShowAllotment: shouldCheck,
    shouldBlockOrder,
  };
}
